import { useEffect, useRef, useState } from "react";
import { ICON_BASE_PATH, COUNTRY_BASE_PATH } from "../../../../utils/config";
import { useTranslation } from "react-i18next";
import {
  fetchCountries,
  type Country,
} from "@/app/services/localservices/countryService";

export interface CountryOption {
  name: string;
  code2: string;
}

interface GlobalCountryProps {
  countries: CountryOption[];
  label?: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  starlable?: boolean;
  disable?: boolean;
  fieldName?: string;
  triggerValidation?: boolean;
  onValidate?: (field: string, isValid: boolean) => void;
}

const getFlagUrl = (country: Country): string => {
  if (country.flagIcon) {
    if (
      country.flagIcon.startsWith("http://") ||
      country.flagIcon.startsWith("https://") ||
      country.flagIcon.startsWith("/")
    ) {
      return country.flagIcon;
    }
    return `${COUNTRY_BASE_PATH}${country.flagIcon}`;
  }
  return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
};

const GlobalCountry: React.FC<GlobalCountryProps> = ({
  countries,
  label = "select country",
  value,
  onChange,
  validationMessage,
  starlable = true,
  disable = false,
  fieldName = "GlobalCountry",
  triggerValidation = false,
  onValidate,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<CountryOption[]>(
    [],
  );
  const [activeIndex, setActiveIndex] = useState(-1);
  const [countryDataList, setCountryDataList] = useState<Country[]>([]);

  const [inputText, setInputText] = useState("");

  const suggestionClickedRef = useRef(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetchCountries()
      .then(setCountryDataList)
      .catch(() => setCountryDataList([]));
  }, []);

  useEffect(() => {
    const match = countries.find((c) => c.code2 === value);
    setInputText(match?.name ?? "");
  }, [value, countries]);

  const getCountryDataByName = (name: string): Country | undefined => {
    if (typeof name !== "string") return undefined;
    return countryDataList.find(
      (c) => c.name.toLowerCase() === name.toLowerCase(),
    );
  };

  const selectedOption = countries.find((c) => c.code2 === value);
  const selectedCountryData = selectedOption
    ? getCountryDataByName(selectedOption.name)
    : undefined;

  const validate = (text: string): string => {
    if (!text) return `${t("please_select_the")} ${label}.`;
    if (touched && text) {
      const exactMatch = countries.some(
        (c) => c.name.toLowerCase() === text.toLowerCase(),
      );
      if (!exactMatch) {
        const hasPartialMatch = countries.some((c) =>
          c.name.toLowerCase().startsWith(text.toLowerCase()),
        );
        if (!hasPartialMatch) {
          return `${t("please_select_valid")} ${label}.`;
        }
      }
    }
    return "";
  };

  const updateValidation = (text: string) => {
    const newError = validate(text);
    setError(newError);
    const isValid = countries.some(
      (c) => c.name.toLowerCase() === text.toLowerCase(),
    );
    onValidate?.(fieldName, isValid);
    return newError;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setInputText(newText);
    setActiveIndex(-1);
    if (value) onChange("");
    const filtered = newText
      ? countries.filter((c) =>
          c.name.toLowerCase().startsWith(newText.toLowerCase()),
        )
      : countries;
    setFilteredCountries(filtered);
    setShowSuggestions(true);

    if (touched && error) {
      const hasPartialMatch = filtered.length > 0;
      if (hasPartialMatch || !newText) setError("");
      else setError(validate(newText));
    }
  };

  const handleSelect = (option: CountryOption) => {
    suggestionClickedRef.current = true;
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    onChange(option.code2);
    setInputText(option.name);
    setShowSuggestions(false);
    setActiveIndex(-1);
    setError("");
    setTouched(true);
    onValidate?.(fieldName, true);
    setTimeout(() => {
      suggestionClickedRef.current = false;
    }, 100);
  };

  const handleFocus = () => {
    const filtered = inputText
      ? countries.filter((c) =>
          c.name.toLowerCase().startsWith(inputText.toLowerCase()),
        )
      : countries;
    setFilteredCountries(filtered);
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    if (suggestionClickedRef.current) return;
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
      setActiveIndex(-1);
      setTouched(true);
      updateValidation(inputText);
      const exactMatch = countries.find(
        (c) => c.name.toLowerCase() === inputText.toLowerCase(),
      );
      if (exactMatch) {
        if (value !== exactMatch.code2) onChange(exactMatch.code2);
      }
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredCountries.length === 0) return;
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = Math.min(activeIndex + 1, filteredCountries.length - 1);
        setActiveIndex(next);
        listRef.current
          ?.querySelectorAll<HTMLDivElement>("[data-item]")
          [next]?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(activeIndex - 1, 0);
        setActiveIndex(prev);
        listRef.current
          ?.querySelectorAll<HTMLDivElement>("[data-item]")
          [prev]?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredCountries.length) {
          handleSelect(filteredCountries[activeIndex]);
        } else if (filteredCountries.length === 1) {
          handleSelect(filteredCountries[0]);
        } else {
          setShowSuggestions(false);
        }
        break;
      }
      case "Escape":
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(inputText);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
      onValidate?.(fieldName, false);
    } else if (touched) {
      updateValidation(inputText);
    }
  }, [validationMessage]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const isInvalid = !!error && touched;

  const defaultSet = useRef(false);

  useEffect(() => {
    if (defaultSet.current) return;

    if (!value && countries.length > 0) {
      const uae = countries.find(
        (c) =>
          c.code2 === "AE" || c.name.toLowerCase() === "united arab emirates",
      );

      if (uae) {
        defaultSet.current = true;
        onChange(uae.code2);
      }
    }
  }, [countries, value, onChange]);

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${inputText ? "floating" : ""}`}
      >
        {t(label)}
        {starlable && (
          <span
            className={`gx_input_star ${
              inputText ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        <div className="gx-country-flag_name">
          <img
            src={`${ICON_BASE_PATH}beneficiarybankcountry.svg`}
            alt="GlobalCountry"
          />

          {selectedCountryData && (
            <img
              src={getFlagUrl(selectedCountryData)}
              alt={selectedCountryData.name}
              className="gx-country-flag"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}

          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disable}
            placeholder=""
            className={`gx-textbox-css ${isInvalid ? "gx-invalid" : "gx-valid"}`}
            aria-invalid={isInvalid}
          />

          {inputText && (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={() => {
                onChange("");
                setInputText("");
              }}
              className="gx-bene-dropdown-clear-btn"
            >
              <img src={`${ICON_BASE_PATH}cross.svg`} alt="Clear" />
            </button>
          )}
        </div>

        {showSuggestions && filteredCountries.length > 0 && (
          <div className="gx-bene-currency-filter" ref={listRef}>
            {filteredCountries.map((option, index) => {
              const countryData = getCountryDataByName(option.name);
              return (
                <div
                  key={option.code2}
                  data-item
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(option);
                  }}
                  className={`gx-country-item gx-country-row ${
                    index === activeIndex ? "bg-blue-50" : "hover:bg-gray-100"
                  }`}
                >
                  {countryData ? (
                    <img
                      src={getFlagUrl(countryData)}
                      alt={option.name}
                      className="gx-flag-img"
                      onError={(e) => {
                        e.currentTarget.style.visibility = "hidden";
                      }}
                    />
                  ) : (
                    <span className="gx-flag-placeholder" />
                  )}
                  <span className="gx-p12-m">{option.name}</span>
                </div>
              );
            })}
          </div>
        )}

        {showSuggestions &&
          filteredCountries.length === 0 &&
          inputText.length > 0 && (
            <div className="gx-bene-currency-no_match">
              <p className="text-sm text-gray-500">
                No matching countries found
              </p>
            </div>
          )}
      </div>

      {isInvalid && (
        <div className="gx_input_validationmessage gx-font-regular">
          {error}
        </div>
      )}
    </div>
  );
};

export default GlobalCountry;
